import {
  IRPCMethods,
  IRPCRequest,
  IRPCResponse,
  IClientTransport,
  RPCMessageType,
  IRPCPublishment,
  ListeningTicket,
  RPCPublishmentName,

} from "@staffbar/types";

import {
  Serializer,
  Deserializer,
} from '../serialization';

import {
  randomIdentifier,
} from '../util';

import {
  RPCResponse
} from '../serialization/rpc';

type ResponseMatcher = (message: IRPCResponse) => boolean
type Resolve<T> = (value?: T | PromiseLike<T>) => void
type Reject = (reason?: any) => void

export default class IFrameClientTransport implements IClientTransport { 

  constructor({ targetWindow, targetOrigin }: { targetWindow: Window, targetOrigin: string }) {
    this._targetWindow = targetWindow
    this._targetOrigin = targetOrigin
  }
  private _targetWindow: Window
  private _targetOrigin: string

  request(rpcName: string, argument:  { [key: string]: any; }): Promise<any> {
    let request: IRPCRequest = {
      id: randomIdentifier(),
      method: rpcName as keyof IRPCMethods,
      parameter: argument,
    }

    let payload = new Serializer(RPCMessageType.Request, request)

    // TODO: Add an automatic timeout
    let promise = this._waitForResponseToMatch((response) => {
      return response.id === request.id
    })
    this._targetWindow.postMessage(
      payload.serialize(), this._targetOrigin
    )
    return promise
  }

  /**
   * In order for `window.removeEventListener` to work we need to pass the same
   * exact function that we used for `addListener`. This is a way to cache that
   * value because `.bind(this)` mutates the original `_onRawMessageRecevied`
   *
   * This also implicitly tracks state about if we're listening or not,
   * `undefined` meaning not listening
   *
   */
  private _listeneingRef: any = undefined

  startListening() {
    if (this._listeneingRef !== undefined) { return; }
    this._listeneingRef = this._onRawMessageReceived.bind(this)
    window.addEventListener('message', this._listeneingRef) 
  }

  stopListening() {
    if (this._listeneingRef === undefined) { return; }
    window.removeEventListener('message', this._listeneingRef)
    this._listeneingRef = undefined
  }

  private _publishmentHandlers: { [key: string]: [ListeningTicket, (publishment: IRPCPublishment) => void][] } = {};

  public registerPublishmentHandler(publishmentName: RPCPublishmentName, handler: (publishment: IRPCPublishment) => void): ListeningTicket {
    let ticket: ListeningTicket = randomIdentifier()
    let currentHandlers = this._publishmentHandlers[publishmentName] || []
    let newHandler: [ListeningTicket, (publishment: IRPCPublishment) => void] = [ticket, handler]
    this._publishmentHandlers[publishmentName] = currentHandlers.concat([newHandler])
    return ticket
  }

  public deRegisterPublishmentHandler(publishmentName: RPCPublishmentName, ticket: ListeningTicket) {
    let currentHandlers = this._publishmentHandlers[publishmentName] || []
    this._publishmentHandlers[publishmentName] = currentHandlers.filter(([storedTicket, _]) => storedTicket !== ticket)
  }

  private _onRawMessageReceived(message: MessageEvent) {
    // Make sure we only listen to messages coming from the target window
    if (message.data) { // && message.origin === this._targetOrigin) {
      this._onMessageReceived(message.data.toString())
    }
  }

  private _onMessageReceived(payload: string) {
    // TODO: Handle other types of messages here
    let deserializer = new Deserializer(payload)
    try {
      switch(deserializer.messageType()) {
        case RPCMessageType.Response: 
          this._onResponseMessageReceived(deserializer.toResponse())
          break;
        case RPCMessageType.Publishment:
          this._onPublishmentMesssageReceived(deserializer.toPublishment())
          break;
      }
    } catch (error) {
      // Something about the message is invalid
      // console.log(`Got Error: ${error}`)
    }
  }

  private _onResponseMessageReceived(response: RPCResponse) {
    var i = this._responseWaiters.length 
    while (i--) {
      const [matcher, resolve, reject] = this._responseWaiters[i]
      if (matcher(response)) {
        // Remove this waiter
        this._responseWaiters.splice(i, 1)
        if (response.success()) {
          resolve(response.toSuccess())
        } else {
          reject(response.toError())
        }
      }
    }
  }

  private _onPublishmentMesssageReceived(publishment: IRPCPublishment) {
    let publishmentName = publishment.publishmentName  
    let [[ticket, handler]] = this._publishmentHandlers[publishmentName].slice(-1)
    if (!ticket) { return }
    handler(publishment)
  }

  private _responseWaiters: [ResponseMatcher, Resolve<IRPCResponse>, Reject][] = []

  private _waitForResponseToMatch(matcher: ResponseMatcher) {
    return new Promise<IRPCResponse>((resolve, reject) => {
      this._responseWaiters.unshift([matcher, resolve as Resolve<IRPCResponse> , reject])
    })
  }
}


/**
 * RPCClient is responsible for making requests to the "inject" 
 * 
 * 
 */

 declare class _RPCClient {
  constructor(clientTransport: IClientTransport) 
}

/**
 * RPCClient accepts method calls and returns a call to client transport
 * 
 */
function _RPCClient(this: any, clientTransport: IClientTransport) {
  const handlers = {
    get(_: any, key: any) {
      return (...args: any[])  => {
        return clientTransport.request(key, args[0]) 
      }
    }
  }

  return new Proxy(this, handlers)
}

export const buildRPCClient = (clientTransport: IClientTransport): IRPCMethods => {
  return new _RPCClient(clientTransport) as IRPCMethods
}

